import { desserializarCenaTabletop, serializarCenaTabletop } from './serializacao'
import type { CenaTabletop } from './tipos'

/**
 * Preset do instrutor: uma montagem que ele salvou para reabrir em aula. Guarda
 * a cena já serializada (o mesmo JSON de Exportar/Importar), então a validação
 * e a migração de formato são as de sempre, e nada de objeto vivo fica
 * guardado por engano.
 */
export interface PresetDoInstrutor {
  readonly id: string
  readonly nome: string
  readonly criadoEm: number
  /** A cena como `serializarCenaTabletop` a escreve. */
  readonly cena: string
}

export const LIMITE_PRESETS_DO_INSTRUTOR = 30
export const TAMANHO_MAXIMO_NOME_PRESET = 60

const nomeLimpo = (nome: string) => nome.trim().replace(/\s+/g, ' ').slice(0, TAMANHO_MAXIMO_NOME_PRESET)

/** Cria o preset a partir da cena aberta. O nome vazio vira o nome da cena. Lança se a cena for inválida. */
export function criarPresetDoInstrutor(cena: CenaTabletop, nome: string, agora: number): PresetDoInstrutor {
  const nomeFinal = nomeLimpo(nome) || nomeLimpo(cena.nome) || 'Montagem sem nome'
  return {
    id: `preset-${agora.toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    nome: nomeFinal,
    criadoEm: agora,
    cena: serializarCenaTabletop(cena),
  }
}

/**
 * Acrescenta o preset no começo da lista. Um nome que já existe substitui o
 * antigo (salvar de novo atualiza a montagem). Acima do limite, descarta os
 * mais antigos.
 */
export function adicionarPresetDoInstrutor(
  lista: readonly PresetDoInstrutor[],
  preset: PresetDoInstrutor,
): PresetDoInstrutor[] {
  const chave = (nome: string) => nome.toLocaleLowerCase('pt-BR')
  const outros = lista.filter((p) => chave(p.nome) !== chave(preset.nome))
  return [preset, ...outros].slice(0, LIMITE_PRESETS_DO_INSTRUTOR)
}

export const removerPresetDoInstrutor = (lista: readonly PresetDoInstrutor[], id: string): PresetDoInstrutor[] =>
  lista.filter((p) => p.id !== id)

/** Abre a cena do preset. Lança se o conteúdo guardado não for mais um table top válido. */
export const cenaDoPresetDoInstrutor = (preset: PresetDoInstrutor): CenaTabletop => desserializarCenaTabletop(preset.cena)

export const serializarPresetsDoInstrutor = (lista: readonly PresetDoInstrutor[]): string => JSON.stringify(lista)

/**
 * Lê a lista guardada. Tolerante: um item quebrado é descartado e o resto fica,
 * porque um preset ruim não pode esconder os outros nem travar o app.
 */
export function desserializarPresetsDoInstrutor(texto: string | null): PresetDoInstrutor[] {
  if (!texto) return []
  let valor: unknown
  try {
    valor = JSON.parse(texto)
  } catch {
    return []
  }
  if (!Array.isArray(valor)) return []
  const ids = new Set<string>()
  const lista: PresetDoInstrutor[] = []
  for (const item of valor as unknown[]) {
    if (typeof item !== 'object' || item === null) continue
    const { id, nome, criadoEm, cena } = item as Record<string, unknown>
    if (typeof id !== 'string' || !id || ids.has(id)) continue
    if (typeof nome !== 'string' || !nomeLimpo(nome)) continue
    if (typeof criadoEm !== 'number' || !Number.isFinite(criadoEm)) continue
    if (typeof cena !== 'string') continue
    try {
      desserializarCenaTabletop(cena)
    } catch {
      continue
    }
    ids.add(id)
    lista.push({ id, nome: nomeLimpo(nome), criadoEm, cena })
  }
  return lista.slice(0, LIMITE_PRESETS_DO_INSTRUTOR)
}
